import { Button, ConfigProvider, Flex, Input, theme } from 'antd'
import { Check, HelpCircle, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { IconWithBackground } from 'src/components'

import { ChatMarkdown } from './ChatMarkdown'
import { useStyles } from './styles'
import type { QuestionResolution } from './types'

const HEADER_ICON_SIZE = 22

type Props = {
  question: string
  options: string[]
  allowMultiple: boolean
  disabled: boolean
  enter?: boolean
  onResolve: (resolution: QuestionResolution) => void
}

export const QuestionCard = ({
  question,
  options,
  allowMultiple,
  disabled,
  enter,
  onResolve
}: Props) => {
  const { styles, cx } = useStyles()
  const { token } = theme.useToken()
  const { t } = useTranslation()
  const [selected, setSelected] = useState<string[]>([])
  const [otherActive, setOtherActive] = useState(false)
  const [other, setOther] = useState('')

  const canSubmit =
    selected.length > 0 || (otherActive && other.trim().length > 0)

  const submit = (nextSelected: string[] = selected) => {
    const finalOther = otherActive ? other.trim() : ''
    if (nextSelected.length === 0 && !finalOther) return
    onResolve({ selected: nextSelected, other: finalOther || undefined })
  }

  const toggleOption = (option: string) =>
    setSelected((prev) =>
      prev.includes(option)
        ? prev.filter((o) => o !== option)
        : [...prev, option]
    )

  const pickOption = (option: string) =>
    allowMultiple ? toggleOption(option) : submit([option])

  const pickOther = () => {
    if (!allowMultiple) setSelected([])
    setOtherActive(true)
  }

  const showSubmit = allowMultiple || otherActive

  const renderOption = (
    key: string,
    label: string,
    active: boolean,
    onClick: () => void
  ) => (
    <button
      key={key}
      type="button"
      disabled={disabled}
      className={cx(
        styles.questionOption,
        active && styles.questionOptionActive
      )}
      onClick={onClick}
    >
      <span
        className={cx(
          allowMultiple ? styles.questionCheckbox : styles.questionRadio,
          active &&
            (allowMultiple
              ? styles.questionCheckboxActive
              : styles.questionRadioActive)
        )}
      >
        {allowMultiple && active ? <Check size={11} strokeWidth={3} /> : null}
      </span>
      <span className={styles.questionOptionLabel}>{label}</span>
    </button>
  )

  return (
    <ConfigProvider theme={{ token: { colorPrimary: token.colorInfo } }}>
      <div className={cx(styles.questionCard, enter && styles.enterSlow)}>
        <Flex align="center" gap={8} className={styles.questionHeader}>
          <IconWithBackground
            size={HEADER_ICON_SIZE}
            shape="square"
            color={token.colorInfo}
          >
            <HelpCircle size={14} />
          </IconWithBackground>
          <span className={styles.questionKicker}>
            {t('workspaceHome.question.clarification')}
          </span>
        </Flex>

        <div className={styles.questionText}>
          <ChatMarkdown content={question} />
        </div>

        <div className={styles.questionOptions}>
          {options.map((option, index) =>
            renderOption(
              `option-${index}`,
              option,
              selected.includes(option),
              () => pickOption(option)
            )
          )}
          {renderOption(
            'other',
            t('workspaceHome.question.otherButton'),
            otherActive,
            pickOther
          )}
        </div>

        {otherActive && (
          <Input
            className={styles.questionOtherRow}
            size="small"
            autoFocus
            disabled={disabled}
            value={other}
            onChange={(e) => setOther(e.target.value)}
            onPressEnter={() => submit()}
            placeholder={t('workspaceHome.question.otherPlaceholder')}
          />
        )}

        {showSubmit && (
          <Flex className={styles.questionActions}>
            <Button
              type="primary"
              size="small"
              icon={<Send size={13} />}
              disabled={disabled || !canSubmit}
              onClick={() => submit()}
            >
              {t('workspaceHome.question.submit')}
            </Button>
          </Flex>
        )}
      </div>
    </ConfigProvider>
  )
}
